import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/pro-light-svg-icons";

import { useUserPreferences } from "utils/hooks";
import { useCurrentOrganization } from "providers/WithCurrentOrganization";

import styles from "./style.module.scss";
import { useRelatedRoute, useRoutes } from "utils/routes";
import { mru } from "utils/array";
import { RecentItem } from "utils/preferences";
import classNames from "classnames";
import {
  CaretIcon,
  CloseIcon,
  ServerStatusType,
  StatusDotIcon,
  StatusLayerGroupIcon,
  StatusLayerIcon,
} from "ui/Icons";
import { faLayerPlus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import Badge from "ui/Badge";
import MenuList, { Section } from "./MenuList";

import { PageHeaderNavFragment } from "./gql/Fragment.generated";
type ServersType = PageHeaderNavFragment["servers"];
type ServerType = ServersType[number];
type ServerGroupsType = PageHeaderNavFragment["serverGroups"];
type ServerGroupType = ServerGroupsType[number];
type CustomServerGroupsType = PageHeaderNavFragment["customServerGroups"];
type SelectableItemBase = {
  id: string;
  name: string;
  depth: number;
  parentId: string | null;
};
type SelectableItem =
  | (SelectableItemBase & { kind: "server"; data: ServerType })
  | (SelectableItemBase & { kind: "serverGroup"; data: ServerGroupType });

const toServerItem = (
  s: ServerType,
  depth: number,
  parentId?: string,
): SelectableItem => ({
  kind: "server",
  data: s,
  id: s.humanId,
  name: s.name,
  depth,
  parentId: parentId ?? null,
});

const toServerGroupItem = (sg: ServerGroupType): SelectableItem => ({
  kind: "serverGroup",
  data: sg,
  id: sg.id,
  name: sg.name,
  depth: 0,
  parentId: null,
});

const sortedClusterMembers = (serverGroup: ServerGroupType) =>
  serverGroup.servers
    .slice()
    .sort((a, b) => {
      const rank = (s: ServerType) =>
        s.latestStats?.isPrimary
          ? 0
          : s.hasCriticalIssues
          ? 1
          : !s.hasRecentSnapshot
          ? 2
          : 3;
      return rank(a) - rank(b);
    })
    .map((s) => toServerItem(s, 1, serverGroup.id));

const ServerSelect = ({
  servers,
  serverGroups,
  customServerGroups,
  currentServerId,
  currentServerGroupId,
  organizationSlug,
}: {
  servers: ServersType;
  serverGroups: ServerGroupsType;
  customServerGroups: CustomServerGroupsType;
  currentServerId?: string;
  currentServerGroupId?: string;
  organizationSlug: string;
}) => {
  const currentOrganization = useCurrentOrganization(true);
  const serverGroupCrudEnabled =
    currentOrganization?.enabledFeatures.serverGroupCrud ?? false;
  const [serverSelect, setServerSelect] = useUserPreferences("serverSelect");
  const recents = useMemo(() => {
    return (serverSelect.recentItems || {})[organizationSlug] || [];
  }, [serverSelect.recentItems, organizationSlug]);
  const recentServerRank = useMemo(() => {
    const m = new Map<string, number>();
    recents
      .filter((r) => r.type === "server")
      .forEach((r, i) => m.set(r.id, i));
    return m;
  }, [recents]);
  const { relatedOrganizationRoute } = useRelatedRoute();

  useEffect(() => {
    const nextRecent: RecentItem | null = currentServerId
      ? { type: "server", id: currentServerId }
      : currentServerGroupId
      ? { type: "serverGroup", id: currentServerGroupId }
      : null;
    if (!nextRecent) {
      return;
    }
    const first = recents[0];
    if (first?.type === nextRecent.type && first?.id === nextRecent.id) {
      return;
    }
    setServerSelect({
      ...serverSelect,
      recentItems: {
        ...serverSelect.recentItems,
        [organizationSlug]: mru(
          nextRecent,
          recents,
          6,
          (a, b) => a.type === b.type && a.id === b.id,
        ),
      },
    });
  }, [
    setServerSelect,
    serverSelect,
    organizationSlug,
    recents,
    currentServerId,
    currentServerGroupId,
  ]);

  const sortedServers = useMemo(
    () =>
      servers.slice().sort((a, b) => {
        const ra = recentServerRank.get(a.humanId);
        const rb = recentServerRank.get(b.humanId);
        if (ra !== undefined && rb !== undefined) {
          return ra - rb;
        }
        if (ra !== undefined) {
          return -1;
        }
        if (rb !== undefined) {
          return 1;
        }
        if (a.hasRecentSnapshot && !b.hasRecentSnapshot) {
          return -1;
        }
        if (!a.hasRecentSnapshot && b.hasRecentSnapshot) {
          return 1;
        }
        return 0;
      }),
    [servers, recentServerRank],
  );

  const serversById = useMemo(() => {
    const m = new Map<string, ServerType>();
    for (const s of servers) {
      m.set(s.humanId, s);
    }
    for (const sg of serverGroups) {
      for (const s of sg.servers) {
        m.set(s.humanId, s);
      }
    }
    return m;
  }, [servers, serverGroups]);

  const serverGroupsByClusterId = useMemo(() => {
    const m = new Map<string, ServerGroupType>();
    for (const sg of serverGroups) {
      if (sg.clusterId) {
        m.set(sg.clusterId, sg);
      }
    }
    return m;
  }, [serverGroups]);

  const currentServer = currentServerId
    ? serversById.get(currentServerId)
    : undefined;

  const currentServerGroup =
    serverGroups.find((sg) => sg.id === currentServerGroupId) ??
    (customServerGroups.find(
      (sg) => sg.id === currentServerGroupId,
    ) as unknown as ServerGroupType | undefined);

  // Cluster member sort order: primary, replica critical, replica no data, replica active.
  const clusterItems = useMemo(() => {
    const sortedGroups = serverGroups
      .filter((sg) => sg.servers.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
    return sortedGroups.flatMap((sg) => [
      toServerGroupItem(sg),
      ...sortedClusterMembers(sg),
    ]);
  }, [serverGroups]);

  const serverItems = useMemo(
    () => sortedServers.map((s) => toServerItem(s, 0)),
    [sortedServers],
  );

  const recentEntries = useMemo(() => {
    const groups = new Map<string, SelectableItem[]>();
    const groupOrder: string[] = [];
    const pushGroup = (key: string, items: SelectableItem[]) => {
      if (!groups.has(key)) {
        groups.set(key, items);
        groupOrder.push(key);
      }
    };
    for (const r of recents) {
      if (r.type === "serverGroup") {
        const sg = serverGroups.find((g) => g.id === r.id);
        if (sg) {
          pushGroup(sg.id, [
            toServerGroupItem(sg),
            ...sortedClusterMembers(sg),
          ]);
        }
        continue;
      }
      const server = serversById.get(r.id);
      if (!server) {
        continue;
      }
      const parent = server.clusterId
        ? serverGroupsByClusterId.get(server.clusterId)
        : undefined;
      if (!parent) {
        pushGroup(`server:${server.humanId}`, [toServerItem(server, 0)]);
        continue;
      }
      const existing = groups.get(parent.id);
      if (existing) {
        if (!existing.some((e) => e.id === server.humanId)) {
          existing.push(toServerItem(server, 1, parent.id));
        }
      } else {
        pushGroup(parent.id, [
          toServerGroupItem(parent),
          toServerItem(server, 1, parent.id),
        ]);
      }
    }
    return groupOrder.flatMap((k) => groups.get(k) ?? []);
  }, [recents, serverGroups, serversById, serverGroupsByClusterId]);

  const RECENTS_MIN_TOTAL = 10;
  const sections = useMemo<Section<SelectableItem>[]>(() => {
    const totalMainEntries = clusterItems.length + serverItems.length;
    const collapsible = clusterItems.length > 0 && serverItems.length > 0;
    return [
      ...(recentEntries.length > 0 && totalMainEntries >= RECENTS_MIN_TOTAL
        ? [
            {
              label: "Recents",
              entries: recentEntries,
              hideOnFilter: true,
              icon: faClock,
              collapsible,
            },
          ]
        : []),
      {
        label: "Clusters",
        entries: clusterItems,
        collapsible,
      },
      {
        label: "Servers",
        entries: serverItems,
        collapsible,
      },
    ].filter((s) => s.entries.length > 0);
  }, [recentEntries, clusterItems, serverItems]);

  const collapsedSections = useMemo(
    () => serverSelect.collapsedSections ?? [],
    [serverSelect.collapsedSections],
  );
  const toggleCollapsed = (label: string) => {
    setServerSelect({
      ...serverSelect,
      collapsedSections: collapsedSections.includes(label)
        ? collapsedSections.filter((l) => l !== label)
        : [...collapsedSections, label],
    });
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [maxDropdownWidth, setMaxDropdownWidth] = useState(Infinity);

  const measureMaxWidth = () => {
    const node = wrapperRef.current;
    if (node) {
      const container = node.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const nodeRect = node.getBoundingClientRect();
        let maxWidth = containerRect.right - nodeRect.left;
        // When no server is selected, DatabaseSelect doesn't render.
        // Add expected width so the dropdown spans the full combined select area.
        if (!currentServer) {
          maxWidth +=
            window.innerWidth >= 1200
              ? 300
              : window.innerWidth >= 800
              ? 160
              : 0;
        }
        setMaxDropdownWidth(maxWidth);
      }
    }
  };

  const maxNameLength = useMemo(
    () =>
      Math.max(
        ...sections.flatMap((s) => s.entries.map((e) => e.name.length)),
        0,
      ),
    [sections],
  );
  // Approximate dropdown width: padding (19px * 2) + status dot (12px) + gap (4px) + name width (~9.5px per char) + badge area (60px)
  const estimatedWidth = 19 * 2 + 12 + 4 + maxNameLength * 9.5 + 60;
  const splitBarColumnWidth = "Add Server Group".length * 9.5 + 60 + 48;
  const minWidth = serverGroupCrudEnabled ? splitBarColumnWidth * 2 : 0;
  const dropdownWidth = Math.min(
    Math.max(estimatedWidth, minWidth),
    maxDropdownWidth,
  );

  const trigger = ({
    open,
    toggleOpen,
  }: {
    open: boolean;
    toggleOpen: () => void;
  }) => {
    const noSelection = `No server ${
      serverGroups.length > 0 ? "or server group " : ""
    }selected`;
    return (
      <button
        onClick={() => {
          measureMaxWidth();
          toggleOpen();
        }}
      >
        <small>{currentServerGroup ? "Server Group" : "Server"}</small>
        <div className={styles.selectMenuCurrent}>
          {currentServer ? (
            <>
              <ServerItemContent server={currentServer} isSelected />
              <Link
                to={relatedOrganizationRoute(organizationSlug)}
                className={styles.closeLink}
              >
                <CloseIcon />
              </Link>
            </>
          ) : currentServerGroup ? (
            <>
              <ServerGroupItemContent serverGroup={currentServerGroup} />
              <Link
                to={relatedOrganizationRoute(organizationSlug)}
                className={styles.closeLink}
              >
                <CloseIcon />
              </Link>
            </>
          ) : (
            <span>{noSelection}</span>
          )}
          <CaretIcon direction={open ? "up" : "down"} headerNav />
        </div>
      </button>
    );
  };

  const prompt = `Search servers${
    serverGroups.length > 0 ? " and groups" : ""
  }...`;
  const noMatch = `no servers ${
    serverGroups.length > 0 ? "or groups " : ""
  }match`;

  return (
    <div
      ref={wrapperRef}
      className={styles.selectMenu}
      data-select-menu="server"
    >
      <MenuList
        trigger={trigger}
        prompt={prompt}
        noMatch={noMatch}
        sections={sections}
        filterKeys={["name"]}
        renderer={SelectableItemRenderer}
        dropdownWidth={dropdownWidth}
        collapsedSections={collapsedSections}
        onToggleCollapsed={toggleCollapsed}
      >
        {serverGroupCrudEnabled ? (
          <div className="mt-3 py-1 -mb-1 border-t border-[#ccc] first:border-t-0 first:pt-0 first:mt-0 grid grid-cols-2">
            <AddServer organizationSlug={organizationSlug} splitBar />
            <AddServerGroup organizationSlug={organizationSlug} />
          </div>
        ) : (
          <AddServer organizationSlug={organizationSlug} />
        )}
      </MenuList>
    </div>
  );
};

const AddServer = ({
  organizationSlug,
  splitBar = false,
}: {
  organizationSlug: string;
  splitBar?: boolean;
}) => {
  const { organizationServersNew } = useRoutes();
  const link = (
    <Link
      key="new"
      className={classNames(
        styles.selectMenuListItem,
        splitBar && [styles.addAction, "w-full !justify-center"],
        "hover:text-[#000] hover:font-medium active:text-[#000] active:font-medium",
      )}
      to={organizationServersNew(organizationSlug)}
      role="button"
      tabIndex={0}
    >
      <FontAwesomeIcon icon={faPlus} className="text-[10px] mr-[2px]" />
      <span className="ml-[4px]">Add Server</span>
    </Link>
  );
  if (splitBar) {
    return link;
  }
  return (
    <div className="mt-3 py-1 -mb-1 border-t border-[#ccc] first:border-t-0 first:pt-0 first:mt-0">
      {link}
    </div>
  );
};

const AddServerGroup = ({ organizationSlug }: { organizationSlug: string }) => {
  const { organizationServerGroupsNew } = useRoutes();
  return (
    <Link
      key="new-group"
      className={classNames(
        styles.selectMenuListItem,
        styles.addAction,
        "w-full !justify-center border-l border-[#eee] hover:text-[#000] hover:font-medium active:text-[#000] active:font-medium",
      )}
      to={organizationServerGroupsNew(organizationSlug)}
      role="button"
      tabIndex={0}
    >
      <FontAwesomeIcon icon={faLayerPlus} className="text-[10px] mr-[2px]" />
      <span className="ml-[4px]">Add Server Group</span>
    </Link>
  );
};

const SelectableItemRenderer = ({
  item,
  onSelect,
}: {
  item: SelectableItem;
  onSelect: () => void;
}) => {
  const { relatedServerGroupRoute, relatedServerRoute } = useRelatedRoute();
  const indentClass = item.depth > 0 ? "ml-[19px]" : "";

  if (item.kind === "server") {
    return (
      <Link
        className={classNames(styles.selectMenuListItem, indentClass)}
        to={relatedServerRoute(item.id)}
        onClick={onSelect}
      >
        <ServerItemContent server={item.data} />
      </Link>
    );
  }
  if (item.kind === "serverGroup") {
    return (
      <Link
        className={styles.selectMenuListItem}
        to={relatedServerGroupRoute(item.id)}
        onClick={onSelect}
      >
        <ServerGroupItemContent serverGroup={item.data} />
      </Link>
    );
  }
  return null;
};

const ServerItemContent = ({
  server,
  isSelected,
}: {
  server: ServerType;
  isSelected?: boolean;
}) => {
  const status = server.hasRecentSnapshot
    ? server.hasCriticalIssues
      ? "critical"
      : "okay"
    : "no data";

  return (
    <>
      {server.clusterId ? (
        <StatusLayerIcon status={status} />
      ) : (
        <StatusDotIcon status={status} />
      )}
      <span
        key={server.name}
        className={classNames("ml-[4px] min-w-0", !isSelected && "flex-1")}
      >
        {server.name}
      </span>
      {isSelected ? (
        server.latestStats?.isPrimary != null && (
          <Badge
            className={classNames(
              "border font-medium flex-shrink-0",
              !server.latestStats.isPrimary &&
                "!bg-[#f0f0f0] !text-[#555] !border-[#ddd]",
            )}
          >
            {server.latestStats.isPrimary ? "primary" : "replica"}
          </Badge>
        )
      ) : (
        <span className="flex-shrink-0 !overflow-visible ml-auto min-w-[60px] text-right flex items-center justify-end">
          {server.latestStats?.isPrimary != null && (
            <Badge
              className={classNames(
                "border font-medium",
                !server.latestStats.isPrimary &&
                  "!bg-[#f0f0f0] !text-[#555] !border-[#ddd]",
              )}
            >
              {server.latestStats.isPrimary ? "primary" : "replica"}
            </Badge>
          )}
        </span>
      )}
    </>
  );
};

const ServerGroupItemContent = ({
  serverGroup,
}: {
  serverGroup: ServerGroupType;
}) => {
  const status: ServerStatusType = serverGroup.servers?.some(
    (s) => s.hasRecentSnapshot && s.hasCriticalIssues,
  )
    ? "critical"
    : (serverGroup.servers ?? []).every((s) => s.hasRecentSnapshot)
    ? "okay"
    : "no data";

  return (
    <>
      <StatusLayerGroupIcon status={status} />
      <span key={serverGroup.name} className="ml-[4px]">
        {serverGroup.name}
      </span>
    </>
  );
};

export default ServerSelect;
